import {
	Component,
	AfterViewInit,
	OnInit,
	EventEmitter,
	Output,
	NgZone,
} from "@angular/core";
import {
	Map,
	LatLng,
	LatLngBounds,
	Marker,
	Icon,
	latLng,
	icon as leafletIcon,
	marker as leafletMarker,
	tileLayer,
} from "leaflet";
import { ActivatedRoute, Router } from "@angular/router";
import { Policies } from "src/app/_core/models/main/policies.enum";

//services
import { TranslateService } from "@ngx-translate/core";
import { LoaderService } from "src/app/_core/providers/loader.service";
import {
	GlobalService,
	MigaDePan,
} from "src/app/_core/providers/global.service";

//models
import { SiteSummaryViewModel } from "src/app/_core/models/master/site-summary.model";
import { ContextLocationViewModel } from "src/app/_core/models/main/context-location.model";
import { LinkItem } from "src/app/_core/models/main/link-item.model";
import { getHomeMenuWithVisited } from "src/app/_core/constants/menu.constants";
import {
	MAP_PIN_ICONS,
	getIconByInstallationType,
	createBasicMapOptions,
	DEFAULT_SPAIN_COORDINATES,
	DEFAULT_VIEW_OPTIONS,
} from "src/app/_core/constants/leaflet-map.constants";
import { SiteViewModel } from "src/app/_core/models/master/site.model";
import { Title } from "@angular/platform-browser";
import { SiteService } from "src/app/_core/providers/site.service";
import { CentrosProcedureViewModel } from "src/app/_core/models/master/centro.model";
import { forEach } from "lodash";

@Component({
	selector: "app-map",
	templateUrl: "./map.component.html",
	styleUrls: ["./map.component.scss"],
})
export class MapComponent implements OnInit, AfterViewInit {
	public contextLocation: ContextLocationViewModel;
	private readonly _menu: LinkItem[] = getHomeMenuWithVisited("/map");

	// Maps
	public options: any;
	public map: Map;
	public layers: any = [];
	public center: LatLng;
	public sites: Array<CentrosProcedureViewModel> = [];
	public allSites: Array<CentrosProcedureViewModel>;
	public fitBounds: LatLngBounds;
	public markerClusterGroup: any; // MarkerClusterGroup comes from plugin
	public markerClusterData: any[] = [];
	public markerClusterOptions: any; // MarkerClusterGroupOptions comes from plugin

	// Filter
	public filterOptions: string[] = [];
	public filterSelection: string[] = [];

	// Selection
	public displayDialog: boolean = false;
	public selectedItem: CentrosProcedureViewModel;
	private readonly currentMarker: Marker;

	constructor(
		private readonly _titleService: Title,
		private readonly _translateService: TranslateService,
		private readonly _zone: NgZone,
		private readonly _router: Router,
		private readonly _global: GlobalService,
		private readonly _loaderService: LoaderService,
		private readonly _siteService: SiteService
	) {
		const miga: MigaDePan[] = [
			{
				nombre: "header.menu.home.map",
				url: window.location.href,
			},
		];
		this._global.limpiarMigas();
		this._global.actualizarMigas(miga);
	}

	// Events
	ngOnInit(): void {
		this._titleService.setTitle(
			this._translateService.instant("title.map")
		);
		this.markerClusterOptions = { maxClusterRadius: 50 };
		this.contextLocation = {
			country: "",
			countryName: "",
			service: "",
			serviceName: "",
			site: "",
			siteName: "",
		};
		this._global.setHeaderOptions([], this.contextLocation, [], this._menu);
		this._loadDataAndMap();
	}

	ngAfterViewInit(): void {}

	// Public methods
	onMapReady(map: Map) {
		this.map = map;
		window.onload = window.onresize = function () {
			map.invalidateSize();
		};
	}

	openDialog(site: CentrosProcedureViewModel) {
		if (site && site.codigo) {
			this.displayDialog = true;
			this.selectedItem = site;
		}
	}

	goToSite(site: SiteViewModel) {
		if (site.hasAccess) {
			this._router.navigate([
				`/service/${  site.serviceCode  }/site/${  site.code}`,
			]);
		}
	}

	closeDialog() {
		this.displayDialog = false;
		this.selectedItem = null;
		this.layers.forEach((layer: Marker) => {
			layer.closePopup();
		});
	}

	filterAllTypes() {
		if (this.filterSelection.length > 0) {
			this.filterSelection = [];
			this._filterSites();
		}
	}

	filterType(option: string) {
		const index = this.filterSelection.indexOf(option);
		if (index == -1) {
			this.filterSelection.push(option);
		} else {
			this.filterSelection.splice(index, 1);
		}
		this._filterSites();
	}

	// Private methods
	private _loadDataAndMap(): void {
		this._loadSites();
	}

	private _setupMapSettings() {
		this.options = createBasicMapOptions();
		this.options.layers[0].addTo(this.map);
		this.map.removeControl(this.map.zoomControl);
	}

	private _initMap(): void {
		this.layers = [];
		const arrayOfLatLngs = [];

		const defaultView = {
			center: latLng(
				DEFAULT_SPAIN_COORDINATES.latitude,
				DEFAULT_SPAIN_COORDINATES.longitude
			),
			zoom: DEFAULT_VIEW_OPTIONS.zoom,
		};

		const {
			pin01: pinIcon01,
			pin02: pinIcon02,
			pin03: pinIcon03,
			pin04: pinIcon04,
			pin05: pinIcon05,
			pin06: pinIcon06,
			pin07: pinIcon07,
		} = MAP_PIN_ICONS;

		for (const site of this.sites) {
			if (!site.latitud || !site.longitud) {
				continue;
			}
			const icon = getIconByInstallationType(site.tipo_Instalacion);

			arrayOfLatLngs.push([
				parseFloat(site.latitud),
				parseFloat(site.longitud),
			]);

			var component = this;
			this.layers.push(
				leafletMarker(
					[parseFloat(site.latitud), parseFloat(site.longitud)],
					{
						icon,
					}
				)
					.bindPopup(site.descripcion)
					.openPopup()
					.on("click", () => {
						component._zone.run(() => {
							component.openDialog(site);
						});
					})
			);
			this.layers;
		}

		// If there are no sites, set the default view to Spain
		if (arrayOfLatLngs.length === 0) {
			this.map.setView(defaultView.center, defaultView.zoom);
		} else {
			this.fitBounds = new LatLngBounds(arrayOfLatLngs);
			this.map.fitBounds(this.fitBounds);
		}
	}

	private _loadSites(): void {
		this._siteService
			.getPublic()
			.subscribe((sites: Array<CentrosProcedureViewModel>) => {
				sites.forEach((s) => {
					const partes = s.codigoTipoInstalacion
						? s.codigoTipoInstalacion.split("_")
						: null;
					if (partes) {
						s.tipo_Instalacion =
							partes.length > 1 ? partes[1] : partes[0];
					}
				});
				this.allSites = sites.filter(
					(x: CentrosProcedureViewModel) =>
						x.latitud &&
						x.longitud &&
						parseFloat(x.latitud) >= -90 &&
						parseFloat(x.latitud) <= 90 &&
						parseFloat(x.longitud) >= -180 &&
						parseFloat(x.longitud) <= 180
				);
				this.filterOptions = [
					...new Set(
						this.allSites
							.filter(
								(p) =>
									p.tipo_Instalacion &&
									p.tipo_Instalacion.trim() !== ""
							)
							.map((p) => p.tipo_Instalacion)
					),
				].sort((a, b) => (a > b ? 1 : -1));
				/* this.filterOptions = [
					...new Map(
						this.allSites
							.filter(
								(p) =>
									p.codigoTipoInstalacion &&
									p.codigoTipoInstalacion.trim() !== ""
							)
							.map((p) => {
								const partes =
									p.codigoTipoInstalacion.split("_");
								return [
									partes.length > 1 ? partes[1] : partes[0],
									p.codigoTipoInstalacion,
								];
							})
					).values(),
				].sort((a, b) => (a > b ? 1 : -1)); */

				this._setupMapSettings();
				this._filterSites();
				this._loaderService.end();
			});
	}

	private _filterSites() {
		if (this.filterSelection.length == 0) {
			this.sites = this.allSites;
		} else {
			this.sites = this.allSites.filter(
				(p) => this.filterSelection.indexOf(p.tipo_Instalacion) > -1
			);
		}
		this._initMap();
	}

	isFilterSelected(filter: string) {
		return this.filterSelection.indexOf(filter) > 0;
	}
}
